import { message, notification } from 'antd';

// Error types and their corresponding messages
const ERROR_MESSAGES = {
    // Network errors
    NETWORK_ERROR: 'Network connection failed. Please check your internet connection.',
    TIMEOUT_ERROR: 'Request timed out. Please try again.',
    CONNECTION_REFUSED: 'Unable to connect to server. Please try again later.',
    
    // Authentication errors
    UNAUTHORIZED: 'You are not authorized to perform this action.',
    FORBIDDEN: 'Access denied. You don\'t have permission to access this resource.',
    TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
    
    // Validation errors
    VALIDATION_ERROR: 'Please check your input and try again.',
    REQUIRED_FIELD: 'This field is required.',
    INVALID_EMAIL: 'Please enter a valid email address.',
    INVALID_PHONE: 'Please enter a valid phone number.',
    PASSWORD_TOO_SHORT: 'Password must be at least 8 characters long.',
    
    // Business logic errors
    ITEM_NOT_FOUND: 'The requested item was not found.',
    ITEM_ALREADY_EXISTS: 'This item already exists.',
    INSUFFICIENT_PERMISSIONS: 'You don\'t have sufficient permissions.',
    OPERATION_NOT_ALLOWED: 'This operation is not allowed.',
    
    // Server errors
    INTERNAL_SERVER_ERROR: 'An internal server error occurred. Please try again later.',
    SERVICE_UNAVAILABLE: 'Service is temporarily unavailable. Please try again later.',
    BAD_GATEWAY: 'Server error. Please try again later.',
    
    // File upload errors
    FILE_TOO_LARGE: 'File size is too large. Please choose a smaller file.',
    INVALID_FILE_TYPE: 'Invalid file type. Please choose a supported file format.',
    UPLOAD_FAILED: 'File upload failed. Please try again.',
    
    // Payment errors
    PAYMENT_FAILED: 'Payment failed. Please try again or use a different payment method.',
    PAYMENT_CANCELLED: 'Payment was cancelled.',
    INSUFFICIENT_FUNDS: 'Insufficient funds for this transaction.',
    
    // Default
    UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

// Error severity levels
export const ERROR_SEVERITY = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical'
};

// Error categories
export const ERROR_CATEGORY = {
    NETWORK: 'network',
    AUTH: 'authentication',
    VALIDATION: 'validation',
    BUSINESS: 'business',
    SERVER: 'server',
    FILE: 'file',
    PAYMENT: 'payment',
    UNKNOWN: 'unknown'
};

class ErrorHandler {
    constructor() {
        this.errorLog = [];
        this.maxLogSize = 100;
    }

    // Determine error type from error object
    getErrorType(error) {
        if (!error) return 'UNKNOWN_ERROR';
        
        // Network errors
        if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
            return 'NETWORK_ERROR';
        }
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            return 'TIMEOUT_ERROR';
        }
        
        // HTTP status codes
        if (error.response) {
            const status = error.response.status;
            switch (status) {
                case 401: return 'UNAUTHORIZED';
                case 403: return 'FORBIDDEN';
                case 404: return 'ITEM_NOT_FOUND';
                case 422: return 'VALIDATION_ERROR';
                case 500: return 'INTERNAL_SERVER_ERROR';
                case 502: return 'BAD_GATEWAY';
                case 503: return 'SERVICE_UNAVAILABLE';
                default: return 'UNKNOWN_ERROR';
            }
        }
        
        // Validation errors
        if (error.name === 'ValidationError' || error.message?.includes('validation')) {
            return 'VALIDATION_ERROR';
        }
        
        return 'UNKNOWN_ERROR';
    }

    // Get user-friendly error message
    getErrorMessage(error, customMessage = null) {
        if (customMessage) return customMessage;
        
        const errorType = this.getErrorType(error);
        
        // Check for specific validation errors
        if (error.response?.data?.errors) {
            const errors = error.response.data.errors;
            const firstError = Object.values(errors)[0];
            if (Array.isArray(firstError)) {
                return firstError[0];
            }
            return firstError;
        }
        
        // Check for custom error message from server
        if (error.response?.data?.message) {
            return error.response.data.message;
        }
        
        return ERROR_MESSAGES[errorType] || ERROR_MESSAGES.UNKNOWN_ERROR;
    }

    // Get error severity
    getErrorSeverity(error) {
        const errorType = this.getErrorType(error);
        
        const severityMap = {
            NETWORK_ERROR: ERROR_SEVERITY.MEDIUM,
            TIMEOUT_ERROR: ERROR_SEVERITY.MEDIUM,
            UNAUTHORIZED: ERROR_SEVERITY.HIGH,
            FORBIDDEN: ERROR_SEVERITY.HIGH,
            TOKEN_EXPIRED: ERROR_SEVERITY.HIGH,
            VALIDATION_ERROR: ERROR_SEVERITY.LOW,
            ITEM_NOT_FOUND: ERROR_SEVERITY.MEDIUM,
            INTERNAL_SERVER_ERROR: ERROR_SEVERITY.CRITICAL,
            SERVICE_UNAVAILABLE: ERROR_SEVERITY.HIGH,
            PAYMENT_FAILED: ERROR_SEVERITY.HIGH,
        };
        
        return severityMap[errorType] || ERROR_SEVERITY.MEDIUM;
    }

    // Get error category
    getErrorCategory(error) {
        const errorType = this.getErrorType(error);
        
        const categoryMap = {
            NETWORK_ERROR: ERROR_CATEGORY.NETWORK,
            TIMEOUT_ERROR: ERROR_CATEGORY.NETWORK,
            UNAUTHORIZED: ERROR_CATEGORY.AUTH,
            FORBIDDEN: ERROR_CATEGORY.AUTH,
            TOKEN_EXPIRED: ERROR_CATEGORY.AUTH,
            VALIDATION_ERROR: ERROR_CATEGORY.VALIDATION,
            ITEM_NOT_FOUND: ERROR_CATEGORY.BUSINESS,
            INTERNAL_SERVER_ERROR: ERROR_CATEGORY.SERVER,
            SERVICE_UNAVAILABLE: ERROR_CATEGORY.SERVER,
            PAYMENT_FAILED: ERROR_CATEGORY.PAYMENT,
        };
        
        return categoryMap[errorType] || ERROR_CATEGORY.UNKNOWN;
    }

    // Log error for debugging
    logError(error, context = {}) {
        const errorLog = {
            id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date().toISOString(),
            type: this.getErrorType(error),
            message: this.getErrorMessage(error),
            severity: this.getErrorSeverity(error),
            category: this.getErrorCategory(error),
            context,
            stack: error.stack,
            userAgent: navigator.userAgent,
            url: window.location.href,
        };
        
        this.errorLog.unshift(errorLog);
        
        // Keep only the latest errors
        if (this.errorLog.length > this.maxLogSize) {
            this.errorLog = this.errorLog.slice(0, this.maxLogSize);
        }
        
        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('Error logged:', errorLog);
        }
        
        // In production, you might want to send this to an error reporting service
        // Example: Sentry.captureException(error, { extra: context });
        
        return errorLog.id;
    }

    // Show error message to user
    showError(error, options = {}) {
        const {
            customMessage = null,
            showNotification = true,
            showMessage = false,
            duration = 5,
            context = {}
        } = options;
        
        const errorId = this.logError(error, context);
        const message = this.getErrorMessage(error, customMessage);
        const severity = this.getErrorSeverity(error);
        
        if (showNotification) {
            notification.error({
                message: 'Error',
                description: message,
                duration,
                key: errorId,
            });
        }
        
        if (showMessage) {
            message.error(message, duration);
        }
        
        return errorId;
    }

    // Show success message
    showSuccess(message, options = {}) {
        const { duration = 3, showNotification = false } = options;
        
        if (showNotification) {
            notification.success({
                message: 'Success',
                description: message,
                duration,
            });
        } else {
            message.success(message, duration);
        }
    }

    // Show warning message
    showWarning(message, options = {}) {
        const { duration = 4, showNotification = false } = options;
        
        if (showNotification) {
            notification.warning({
                message: 'Warning',
                description: message,
                duration,
            });
        } else {
            message.warning(message, duration);
        }
    }

    // Show info message
    showInfo(message, options = {}) {
        const { duration = 3, showNotification = false } = options;
        
        if (showNotification) {
            notification.info({
                message: 'Info',
                description: message,
                duration,
            });
        } else {
            message.info(message, duration);
        }
    }

    // Get error log
    getErrorLog() {
        return this.errorLog;
    }

    // Clear error log
    clearErrorLog() {
        this.errorLog = [];
    }

    // Handle API errors specifically
    handleApiError(error, context = {}) {
        // Check if it's a token expiration error
        if (error.response?.status === 401) {
            // Clear user data and redirect to login
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
        }
        
        return this.showError(error, {
            showNotification: true,
            context
        });
    }

    // Handle form validation errors
    handleValidationError(error, form = null) {
        if (error.response?.data?.errors && form) {
            // Set form field errors
            const errors = error.response.data.errors;
            const formErrors = {};
            
            Object.keys(errors).forEach(field => {
                formErrors[field] = {
                    errors: [errors[field][0]]
                };
            });
            
            form.setFields(formErrors);
        }
        
        return this.showError(error, {
            customMessage: 'Please check the form and try again.',
            showMessage: true
        });
    }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

export default errorHandler;
